import dayjs from 'dayjs'
import React from 'react'
import { Badge, Tag } from 'antd'
import { formatPrice } from '../../utils'
import { Value } from '../../elements'
import { DataType, IMeasurementDates, StagesPops } from './interface'
import { Sorter } from '../../../interfaces'
import { Stage } from '../../../components/activity-timeline/styles'
import { RowActionMenu } from '../../../components/row-action-menu'
import { NotesCell } from '../../../components/v2/notes-cell'
import { OrderProcessProgress } from './process-progress'
import { EditableColumnType } from '../../../components/data-table/interface'
import { getCancelReasonDescription } from '../../../constants/cancel-reason-descriptions'
import { DescriptionTooltipIcon } from '../../../components/description-tooltip-icon'

/**
 * Приводит номер телефона к виду +A (BBB) CCC-DD-EE
 * */
export const formatPhoneNumber = (phoneNumber?: string) => {
  if (!phoneNumber) {
    return <Value data={undefined} />
  }
  if (!phoneNumber.startsWith('7')) {
    return phoneNumber
  }
  const cleaned = ('' + phoneNumber).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})$/)
  if (match) {
    return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}-${match[5]}`
  }
  return phoneNumber
}

const ColumnId: EditableColumnType<DataType>[] = [
  {
    title: 'ID',
    dataIndex: 'id',
    key: 'id',
    width: 70,
    fixed: 'left',
    render: (_, { id, stage }) => {
      return <span style={stage?.code === 'new' ? { fontWeight: 'bold' } : {}}>{id}</span>
    },
  },
  {
    title: 'Стадия',
    dataIndex: 'stage',
    key: 'stage',
    width: 170,
    render: (_, row) => {
      const { stage } = row
      return <StageContainer stage={stage} />
    },
  },
]

const ColumnOrganization: EditableColumnType<DataType>[] = [
  {
    title: 'Бренд',
    width: 200,
    dataIndex: 'brand',
    key: 'brand',
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Офис',
    width: 200,
    dataIndex: 'office',
    key: 'office',
    sorter: true,
    render: val => <Value data={val} />,
  },
]

const counterStyle = { display: 'flex', justifyContent: 'center' }

const ColumnsGeneral: EditableColumnType<DataType>[] = [
  {
    title: 'Чаты',
    dataIndex: 'count_unread_messages',
    key: 'count_unread_messages',
    width: 60,
    sorter: true,
    render: val => <div style={counterStyle}>{val ? <Badge count={val} /> : '-'}</div>,
  },
  {
    title: 'Заметки',
    dataIndex: 'notes_count',
    key: 'notes_count',
    width: 80,
    align: 'center',
    render: (count, record) => <NotesCell cardId={record.id} count={count} />,
  },
  {
    title: 'Менеджер',
    dataIndex: 'manager_name',
    key: 'manager_name',
    width: 200,
    sorter: true,
    render: val => <Value data={val} />,
  },
  {
    title: 'Дата обращения',
    dataIndex: 'appeal_date',
    key: 'appeal_date',
    width: 130,
    sorter: true,
    render: val => dayjs(val).format('DD.MM.YYYY HH:mm'),
  },
  {
    title: 'В работе менеджера с',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 130,
    sorter: true,
    render: val => dayjs(val).format('DD.MM.YYYY HH:mm'),
  },
  {
    title: 'Дата обновления',
    dataIndex: 'updated_at',
    key: 'updated_at',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).fromNow() : null),
  },
  {
    title: 'Дата задачи',
    dataIndex: 'task_date',
    key: 'task_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY HH:mm') : null),
  },
  {
    title: 'Дата окончания договора',
    dataIndex: 'contract_end_date',
    key: 'contract_end_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Контактное лицо клиента',
    dataIndex: 'name',
    width: 120,
  },
  {
    title: 'Телефон клиента',
    dataIndex: 'phone',
    key: 'phone',
    width: 150,
    render: val => formatPhoneNumber(val),
  },
  {
    title: 'Авито ID',
    dataIndex: 'avito_id',
    key: 'avito_id',
    width: 100,
  },
  {
    title: 'Вид контакта',
    dataIndex: 'source_appeal',
    key: 'source_appeal',
    width: 120,
    render: val => <Value data={val} />,
  },
  {
    title: 'Ход работ',
    dataIndex: 'process_progress',
    key: 'process_progress',
    width: 250,
    className: 'no-edit',
    maxLines: null,
    render: (_, row) => <OrderProcessProgress row={row} />,
  },
  {
    title: 'Признак заказа',
    dataIndex: 'order_attribute',
    key: 'order_attribute',
    width: 120,
    render: val => <Value data={val} />,
  },
  {
    title: 'Адрес объекта',
    dataIndex: 'address',
    key: 'address',
    width: 120,
    render: val => <Value data={val} />,
  },
  {
    title: 'Примечание к адресу',
    dataIndex: 'address_note',
    key: 'address_note',
    width: 140,
    render: val => <Value data={val} />,
  },
  {
    title: 'Сумма заказа',
    dataIndex: 'products_total',
    key: 'products_total',
    width: 130,
    sorter: true,
    render: val => formatPrice(val || 0),
  },
  {
    title: 'Предоплата',
    dataIndex: 'payments_total',
    key: 'payments_total',
    width: 130,
    sorter: true,
    render: val => formatPrice(val || 0),
  },
  {
    title: 'Задолженность',
    dataIndex: 'debt',
    key: 'debt',
    width: 130,
    sorter: true,
    render: val => {
      const debt = formatPrice(val || 0)
      const style = +val > 0 ? { color: 'red', fontWeight: 'bold' as const } : {}
      return <span style={style}>{debt}</span>
    },
  },
  {
    title: 'Плановая дата замера',
    dataIndex: 'measurement_date_plan',
    key: 'measurement_date_plan',
    width: 130,
    sorter: true,
    render: (values?: IMeasurementDates[]) => {
      if (values?.length) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '3px',
            }}
          >
            {values.map(({ id, date }, k) => (
              <Tag key={k}>
                №{id} - {date ? dayjs(date).format('DD.MM.YYYY') : 'Не установлено'}
              </Tag>
            ))}
          </div>
        )
      }
      return <Value data={values} />
    },
  },
  {
    title: 'Дата замера',
    dataIndex: 'measurement_date',
    key: 'measurement_date',
    width: 130,
    sorter: true,
    render: (values?: IMeasurementDates[]) => {
      if (values?.length) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '3px',
            }}
          >
            {values.map(({ id, date }, k) => (
              <Tag key={k}>
                №{id} - {date ? dayjs(date).format('DD.MM.YYYY') : 'Не установлено'}
              </Tag>
            ))}
          </div>
        )
      }
      return <Value data={values} />
    },
  },
  {
    title: 'Дата внесения предоплаты',
    dataIndex: 'prepayment_date',
    key: 'prepayment_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Дата окончательной оплаты',
    dataIndex: 'repayment_date',
    key: 'repayment_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Плановая дата доставки',
    dataIndex: 'delivery_date_plan',
    key: 'delivery_date_plan',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Дата доставки',
    dataIndex: 'delivery_date',
    key: 'delivery_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Плановая дата монтажа',
    dataIndex: 'mount_date_plan',
    key: 'mount_date_plan',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Дата монтажа',
    dataIndex: 'mount_date',
    key: 'mount_date',
    width: 130,
    sorter: true,
    render: val => (val ? dayjs(val).format('DD.MM.YYYY') : null),
  },
  {
    title: 'Номер производственного заказа',
    dataIndex: 'production_number',
    key: 'production_number',
    width: 180,
    render: val => <Value data={val} />,
  },
  {
    title: 'Номер договора',
    dataIndex: 'contract_number',
    key: 'contract_number',
    width: 120,
    render: val => <Value data={val} />,
    sorter: true,
  },
  {
    title: 'Причина отмены',
    width: 200,
    dataIndex: 'case_cancel_reason',
    key: 'case_cancel_reason',
    render: val => {
      const description = getCancelReasonDescription(val)
      const value = <Value data={val} />

      return description ? (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            maxWidth: '100%',
          }}
        >
          <span>{value}</span>
          <DescriptionTooltipIcon description={description} placement="top" />
        </span>
      ) : (
        value
      )
    },
  },
  {
    title: 'Примечание к отмене',
    width: 240,
    dataIndex: 'cancel_note',
    key: 'cancel_note',
    render: val => <Value data={val} />,
  },
  {
    title: 'Дата создания',
    dataIndex: 'created_at',
    key: 'created_at',
    width: 130,
    sorter: true,
    render: val => <span>{dayjs(val).fromNow()}</span>,
  },
]

const ColumnAction: EditableColumnType<DataType>[] = [
  {
    title: '',
    dataIndex: 'operation',
    key: 'operation',
    className: 'no-edit',
    fixed: 'right',
    width: 50,
    align: 'center',
    render: (_, { id, deleteRecord }) => {
      return (
        <span
          onClick={e => {
            e.stopPropagation()
          }}
        >
          <RowActionMenu id={Number(id)} deleteRecord={deleteRecord as any} />
        </span>
      )
    },
  },
]

export function getColumns(leader: boolean, sorter: Sorter) {
  const columns = leader
    ? ColumnId.concat(ColumnOrganization, ColumnsGeneral, ColumnAction)
    : ColumnId.concat(ColumnsGeneral)

  return columns.map(item => {
    if (item.key === sorter.sort_field) {
      item.sortOrder = sorter.sort_order
    } else {
      delete item.sortOrder
    }
    return item
  })
}

// удаление вынесено в RowActionMenu

export function StageContainer({ stage }: { stage: StagesPops }) {
  return <Stage style={stage?.style}>{stage?.name}</Stage>
}
